import { useResolveGoalParentChanges, useSortedGoals } from 'features/goals';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import { TreeSectionHeader } from 'shared/components/ui/tree-section-header';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  TreeItemMove,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { ID } from 'shared/types/id';
import { goalInboxID } from 'shared/types/orderable-section';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useGoalGroupingGoalListTree } from '../../hooks/use-goal-grouping-goal-list-tree';
import { useOpenCreateInList } from '../../hooks/use-open-create-in-list';
import { GroupingGoalListProps } from '../../types';
import { TreeGoalCard } from '../tree-goal-card';
import { GoalGroupHeader } from './goal-group-header';
import * as Styled from './goal-grouping-goal-list.style';
import { resolveProjectGroupMoves } from './resolve-project-group-moves';

// Projects can be dragged to reorder within a parent-goal group or moved onto a
// different group or sub-goal folder (re-parented). Projects are leaves — a
// project can't nest under another project — so `canDropOnItem` keeps them out.
const droppedTypes = {
  [TaskListItemTypes.Root]: [],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
  [TaskListItemTypes.GoalDeadline]: [],
};

const dragTypes = [TaskListItemTypes.Goal];

export const GoalGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  goalChildrenByParentId,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  onUpdateProgress,
  hideArchived,
  hideCompleted,
  rootLink,
  showParentGoalLabel,
  showParentLifeAreaLabel,
  showParentLabelsForGoals,
  nestGoals,
  isProjectList,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();
  const openCreateInList = useOpenCreateInList(isProjectList);
  const sortedGoals = useSortedGoals();

  const parentGoals = useMemo(
    () => sortedGoals.map(({ id, name }) => ({ id, name })),
    [sortedGoals],
  );

  const projectsMap = useMemo(
    () => new Map(goals.map((goal) => [goal.id, goal])),
    [goals],
  );
  const allGoalsMap = useMemo(
    () => new Map(sortedGoals.map((goal) => [goal.id, goal])),
    [sortedGoals],
  );

  const treeItems = useGoalGroupingGoalListTree(goals, parentGoals, {
    hideArchived,
    hideCompleted,
    goalChildrenByParentId,
    nestGoals,
  });

  // Only the listed projects move; sub-goal folders are grouping structure here.
  const isItemDraggable = useCallback(
    (item: TreeItem) => projectsMap.has(item.id),
    [projectsMap],
  );
  const canDropOnItem = useCallback(
    (target: TreeItem) => !projectsMap.has(target.id),
    [projectsMap],
  );

  const { close, position, payload } = useContextMenu();
  const resolveGoalParentChanges = useResolveGoalParentChanges();
  const links = useEntityLinks();

  const onTreeReorder = (updatedItems: TreeItem[], moves: TreeItemMove[]) => {
    if (!onReorder) {
      return;
    }

    const updatedSections: { id: ID; goalSorting: ID[] }[] = [];

    const goalParentChanges = resolveGoalParentChanges(moves, [
      rootId,
      goalInboxID,
    ]);

    // Root sections and sub-goal folders both own a goalSorting; projects never do.
    updatedItems.forEach((item) => {
      const isGoalFolder =
        item.type === TaskListItemTypes.Goal && !projectsMap.has(item.id);
      if (item.type === TaskListItemTypes.LifeArea || isGoalFolder) {
        updatedSections.push({ id: item.id, goalSorting: item.children ?? [] });
      }
    });

    const updatedGoals = resolveProjectGroupMoves({
      updatedItems,
      treeItems,
      projectsMap,
      allGoalsMap,
      links,
    });

    onReorder(updatedSections, updatedGoals, [], goalParentChanges);
  };

  const renderItem = useCallback(
    ({ itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      const handleAddToGroup = () => {
        const groupGoal = allGoalsMap.get(itemId);
        openCreateInList({
          ancestorIds: groupGoal
            ? [...(groupGoal.ancestorIds ?? []), groupGoal.id]
            : [itemId],
        });
      };

      // A sub-goal folder is grouping structure, so it gets the group header, not a goal row.
      if (
        itemData.type === TaskListItemTypes.Goal &&
        !projectsMap.has(itemId)
      ) {
        return (
          <GoalGroupHeader item={itemInstance} onAddGoal={handleAddToGroup} />
        );
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = allGoalsMap.get(itemId);
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            rootLink={rootLink}
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            hideLifeArea
            showParentGoalLabel={showParentGoalLabel}
            showParentLifeAreaLabel={showParentLifeAreaLabel}
            showParentLabelsForGoals={showParentLabelsForGoals}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (
        itemId === ARCHIVED_SECTION_ID ||
        itemId === COMPLETED_SECTION_ID ||
        itemId === goalInboxID
      ) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;
        const isArchived = itemId === ARCHIVED_SECTION_ID;
        const isCompleted = itemId === COMPLETED_SECTION_ID;
        const isStaticSection = isArchived || isCompleted;
        const handleAddToList = () => openCreateInList({});

        const sectionTitle = isArchived
          ? t('pages.goals.archived.title')
          : isCompleted
            ? t('pages.goals.completed.title')
            : itemData.name;

        return (
          <TreeSectionHeader
            item={itemInstance}
            lifeArea={undefined}
            overrideTitle={sectionTitle}
            overrideIcon={null}
            disableLink
            useSecondaryTitle={isStaticSection}
            isFirst={isFirst}
            onAddGoal={isStaticSection ? undefined : handleAddToList}
          />
        );
      }

      return (
        <GoalGroupHeader
          item={itemInstance}
          isFirst={(treeItems.root.children ?? [])[0] === itemId}
          onAddGoal={handleAddToGroup}
        />
      );
    },
    [
      treeItems,
      rootLink,
      allGoalsMap,
      projectsMap,
      onUpdateProgress,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
      showParentGoalLabel,
      showParentLifeAreaLabel,
      showParentLabelsForGoals,
      openCreateInList,
      t,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={onReorder ? onTreeReorder : undefined}
        dropTypes={droppedTypes}
        dragTypes={onReorder ? dragTypes : []}
        isItemDraggable={isItemDraggable}
        canDropOnItem={canDropOnItem}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
